
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Settings, Monitor, Megaphone, Briefcase, ChartBar, Users } from "lucide-react";
import TimeComparison from "../components/TimeComparison";
import Integrations from "../components/Integrations";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

const Solutions = () => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  const [cardsRef, cardsVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  const [displayedTitle, setDisplayedTitle] = useState<string[]>([]);
  const titleWords = ["Bridging", "the", "gap", "between", "marketing", "&", "finance"];

  useEffect(() => {
    if (isVisible) {
      const animateWords = async () => {
        for (let i = 0; i < titleWords.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, 150));
          setDisplayedTitle(prev => [...prev, titleWords[i]]);
        }
      };
      animateWords();
    }
  }, [isVisible]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="relative bg-[#FAF9F8]">
        <div 
          ref={ref}
          className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 pb-16"
        >
          <h1 className="text-[50px] md:text-[65px] lg:text-[80px] leading-[1.1] md:leading-[1] font-charter font-medium tracking-tighter text-[#141413] mb-8">
            <div className="flex flex-wrap justify-center gap-x-3 md:gap-x-5">
              <div className="flex flex-wrap justify-center gap-x-3 md:gap-x-5 basis-full md:basis-auto">
                {titleWords.slice(0, 4).map((word, index) => (
                  <span
                    key={index}
                    className={`transition-all duration-300 transform ${
                      displayedTitle.includes(word)
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-8'
                    }`}
                  >
                    {word}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap justify-center gap-x-3 md:gap-x-5 w-full md:mt-2">
                {titleWords.slice(4).map((word, index) => (
                  <span
                    key={index + 4}
                    className={`transition-all duration-300 transform ${
                      displayedTitle.includes(word)
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-8'
                    }`}
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          </h1>
          
          <div 
            className={`max-w-[600px] mx-auto transition-all duration-700 delay-700 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-base md:text-xl text-[#141413]/80 text-center">
              From small teams to industry leaders, we simplify complex data so you can focus on growth.
            </p>
          </div>
        </div>
      </div>

      <TimeComparison />

      <div className="py-4 md:py-6 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div 
            ref={cardsRef}
            className={`bg-white rounded-2xl p-4 md:p-8 transition-all duration-1000 transform ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-charter text-center mb-8 md:mb-12">
              Solutions for all businesses
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Agencies
                </h2>
                <p className="text-sm md:text-base text-white">
                  Stop wasting time juggling multiple client reports - get all your marketing and financial data in one place.
                </p>
              </div>

              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <Monitor className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Media Buyers
                </h2>
                <p className="text-sm md:text-base text-white">
                  Eliminate wasted ad spend with real-time insights that show what's working and what's not.
                </p>
              </div>

              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <Megaphone className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Marketing Managers
                </h2>
                <p className="text-sm md:text-base text-white">
                  Prove the ROI of your marketing efforts without spending hours on spreadsheets.
                </p>
              </div>

              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <Briefcase className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Brand Owners
                </h2>
                <p className="text-sm md:text-base text-white">
                  No more guessing if your marketing is profitable - see exactly how your spend translates into revenue.
                </p>
              </div>

              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <ChartBar className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Data Analysts
                </h2>
                <p className="text-sm md:text-base text-white">
                  Ditch manual data aggregation - get automated reports that deliver insights instantly.
                </p>
              </div>

              <div className="bg-[#104B60] shadow-sm p-4 md:p-8 rounded-2xl">
                <h2 className="text-xl md:text-2xl font-charter mb-2 md:mb-4 flex items-center gap-2 text-white">
                  <Settings className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  Ops Managers
                </h2>
                <p className="text-sm md:text-base text-white">
                  Stop the disconnect between marketing and finance - get full visibility into business performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Integrations />
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 mt-8 md:mt-16 mb-8 md:mb-16">
        <div className="w-full border-2 border-[#27B5FC] bg-[#27B5FC] rounded-xl md:rounded-2xl">
          <div className="text-center space-y-4 md:space-y-6 p-6 md:p-8">
            <h2 className="text-4xl md:text-6xl font-charter font-medium text-white leading-tight">
              Ready to get<br />started today?
            </h2>
            <Button 
              className="w-full md:w-auto bg-[#FAF9F8] hover:bg-[#104B60] text-black hover:text-white px-4 md:px-6 py-3 md:py-4 text-base md:text-lg h-auto rounded-full transition-colors"
              onClick={() => window.location.href = 'https://calendly.com/plannifi-demo/30min?back=1'}
            >
              Book a Free Demo
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Solutions;
